import React from "react"
import BlockContent from "@sanity/block-content-to-react"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"

import { useImage } from "../../hooks/useImage"
import { useRoutes } from "../../hooks/useRoutes"

import { StyledUl, StyledOl, StyledLi } from "../Styled/List"
import { StyledH1, StyledH2, StyledH3, StyledH4, StyledH5, StyledLabel, StyledP, Blockquote } from "../Styled/Text"
import { ContentButton } from "./RichTextStyles"

const Components = {
  h1: StyledH1,
  h2: StyledH2,
  h3: StyledH3,
  h4: StyledH4,
  h5: StyledH5,
  h6: StyledLabel,
  blockquote: Blockquote,
  normal: StyledP,
}

const BlockComponents = props => {
  const { style } = props.node || {}
  const Component = Components[style]

  return Component ? <Component>{props.children}</Component> : BlockContent.defaultSerializers.types.block(props)
}

/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
export const withRichText =
  Component =>
  ({ name = `RichText`, children, sanity, ...props }: { name?: string; children?: any; sanity?: boolean }): JSX.Element => {
    Component.displayName = name

    const { getFluidImage } = useImage()
    const { linkResolver } = useRoutes()

    const serializers = {
      marks: {
        document: ({ children, mark }) => {
          const link = linkResolver(mark?.document)

          return (
            <ContentButton as={Link} colour={`minimal`} size={`link`} title={mark?.document?.title || link?.title || children} to={link?.url}>
              {children}
            </ContentButton>
          )
        },
        link: ({ children, mark }) => (
          <ContentButton
            as={`a`}
            colour={`minimal`}
            href={mark?.link?.link}
            size={`link`}
            target={mark?.link?.external && `_blank`}
            title={mark?.link?.title || children}
          >
            {children}
          </ContentButton>
        ),
      },
      list: ({ children, type }) => (type === `number` ? <StyledOl>{children}</StyledOl> : <StyledUl>{children}</StyledUl>),
      listItem: ({ children, node }) => <StyledLi level={node?.level}>{children}</StyledLi>,
      types: {
        code: ({ node }) => <div dangerouslySetInnerHTML={{ __html: node.code }} />,
        customImage: ({ node }) => {
          node.asset ? (node.asset.id = node.asset._ref) : null
          return <GatsbyImage image={{ ...getFluidImage(node, { maxWidth: 2000 }) }} alt={node?.alt || ""} />
        },
        block: BlockComponents,
      },
    }

    return children ? (
      <>
        {sanity ? (
          <Component {...props}>
            <BlockContent blocks={children} serializers={serializers} />
          </Component>
        ) : (
          <Component {...props}>
            <div dangerouslySetInnerHTML={{ __html: children }} />
          </Component>
        )}
      </>
    ) : null
  }
